import { useEffect, useState } from 'react';
import { apiGet, ApiError } from '../lib/apiClient';

/**
 * Care-plan health on the Dashboard (docs/administration.md §24.5).
 *
 * This is C2a of a split: counts only, no plan content. A care plan is clinical
 * content about an identifiable patient, and plan-content visibility is C2b,
 * pending the PHI access decisions in §24.5.4. There is deliberately nothing here
 * to scope by role, because there is nothing sensitive to scope.
 *
 * The staleness window comes from the payload so this file never has to agree with
 * the backend about the number.
 */
export interface CarePlanMetrics {
  draft: number;
  active: number;
  inProgress: number;
  completed: number;
  stalled: number;
  stalledAfterDays: number;
}

export interface CarePlanTile {
  key: 'active' | 'inProgress' | 'completed' | 'stalled';
  label: string;
  value: number;
  meta: string;
  /** True for the one tile that implies someone should do something. */
  actionable: boolean;
}

export function formatCarePlanTiles(m: CarePlanMetrics): CarePlanTile[] {
  return [
    {
      key: 'active',
      label: 'Active plans',
      value: m.active,
      meta: 'Signed and awaiting patient progress',
      actionable: false,
    },
    {
      key: 'inProgress',
      label: 'In progress',
      value: m.inProgress,
      meta: 'Patient working through items',
      actionable: false,
    },
    {
      key: 'completed',
      label: 'Completed',
      value: m.completed,
      meta: 'All items done',
      actionable: false,
    },
    // The only number that implies an action: a plan a patient signed and then
    // stopped. `draft` is not a tile — an unsigned plan has never reached a
    // patient, so it is not an oversight concern.
    {
      key: 'stalled',
      label: 'Stalled',
      value: m.stalled,
      meta: `Untouched for over ${m.stalledAfterDays} days`,
      actionable: true,
    },
  ];
}

export function CarePlanPanel() {
  const [metrics, setMetrics] = useState<CarePlanMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiGet<CarePlanMetrics>('/admin/dashboard/care-plans')
      .then((data) => {
        if (!cancelled) setMetrics(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load care-plan metrics');
        }
      })
      .finally(() => {
        if (!cancelled) setPending(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Mirrors Dashboard's own placeholder so the numeric slot never collapses. */
  const figure = (value: number | undefined) =>
    pending ? '…' : error && value === undefined ? '—' : (value ?? 0);

  const tiles = metrics ? formatCarePlanTiles(metrics) : [];

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Care plans</h2>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="stat-card-grid">
        {(tiles.length
          ? tiles
          : ([
              { key: 'active', label: 'Active plans', value: undefined, meta: '', actionable: false },
              { key: 'inProgress', label: 'In progress', value: undefined, meta: '', actionable: false },
              { key: 'completed', label: 'Completed', value: undefined, meta: '', actionable: false },
              { key: 'stalled', label: 'Stalled', value: undefined, meta: '', actionable: true },
            ] as Array<Omit<CarePlanTile, 'value'> & { value: number | undefined }>)
        ).map((tile) => (
          <div className="card stat-card" key={tile.key}>
            <div className="card-header">
              <div className="card-title">{tile.label}</div>
            </div>
            <div className="card-body">
              <span className="stat-card-value">{figure(tile.value)}</span>
              <span className="stat-card-meta">{tile.meta}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CarePlanPanel;
